import '../sass/index.scss';
import ScrollManager from './manager/ScrollManager';
import { initWebgl } from './webgl/core/GL.js';
import webglConfig from './webgl/webgl.js';
import Time from './utils/performance/TimeManager.js';
import QualityManager from './utils/performance/QualityManager.js';
/// #if __DEBUG__
/// #code import DebugManager from './utils/debug/DebugManager.js';
/// #endif
import DeviceManager from './utils/device/DeviceManager.js';
import ViewportManager from './utils/viewport/ViewportManager.js';
import KbdController from './utils/input/KbdController.js';
import TouchController from './utils/input/TouchController.js';
import AudioManager from './utils/audio/AudioManager.js';
import Preloader from './preloader/Preloader.js';
import HomePage from './page/HomePage.js';

// All static imports above (including the CSS) have already resolved by this point.
document.documentElement.classList.add('css-ready');

export default class App {
  constructor(){
    this.isLoaded = false;

    this.size = {
      x: window.innerWidth,
      y: window.innerHeight
    }

    this.ratioScreen = this.size.x / this.size.y
    this.devicePixelRatio = window.devicePixelRatio;

    this.deviceManager = new DeviceManager();
    /// #if __DEBUG__
    /// #code this.debugManager = new DebugManager();
    /// #endif
    this.viewport = new ViewportManager();
    this.kbd = new KbdController();
    this.touch = new TouchController({ viewport: this.viewport });
    this.audio = new AudioManager();

    this.homePage = new HomePage({ touch: this.touch });

    this.GL = initWebgl(webglConfig)

    this.qualityManager = new QualityManager()
    this.GL.setQualityManager(this.qualityManager)
    /// #if __DEBUG__
    /// #code this.debugManager.initQuality(this.qualityManager)
    /// #code this.debugManager.initPreloader()
    /// #endif

    this.deviceManager.gpuDetectionFinished.then(() => {
      this.deviceManager.updateQuality(this.qualityManager.quality ?? this.deviceManager.gpu.qualityIndex)
    })

    this.scrollManager = new ScrollManager()

    this.isMobile = window.matchMedia('(pointer: coarse)').matches

    this.bindMethods();
    this.events()

    this.kbd.listen();
    this.touch.listen();

    Time.add(this.render)
    Time.add(this.tickScroll)
    Time.start()

    this.init()
  }


  bindMethods() {
    this.render = this.render.bind(this)
    this.tickScroll = this.tickScroll.bind(this)
    this.resize = this.resize.bind(this)
  }

  events() {
    window.addEventListener('resize', this.resize)
    window.addEventListener('orientationchange', this.resize)
  }

  init() {
    this.preloaderEl = document.querySelector('#preloader')
    this.preloader = this.preloaderEl ? Preloader(this.preloaderEl) : null
    this.preloader?.enter()

    const skipPreloader = sessionStorage.getItem('debug:skip-preloader') === 'true'

    Promise.all([this.GL.preload()])
      .then(() => {
        this.GL.start()
        this.isLoaded = true;
        this.scrollManager.start()
        this.resize();
      })
      .then(() => {
        if (skipPreloader) this.preloaderEl?.remove()
        else this.preloader?.exit(() => {})
      })
  }

  resize(){
    this.devicePixelRatio = window.devicePixelRatio;
    this.size.x = window.innerWidth;
    this.size.y = window.innerHeight;

    this.GL.resize()
  }

  tickScroll(time) {
    this.scrollManager.render(time.elapsed)
  }

  render() {
    this.kbd.poll();
    this.touch.poll();
    this.audio.update(Time.clampedDt);
    this.homePage.update(Time.clampedDt);

    if (!this.isLoaded) return;

    const cpuStart = performance.now();

    this.GL.update()
    this.GL.render()

    if (this.qualityManager) this.qualityManager.setCpuTime(performance.now() - cpuStart)
  }

}

new App();
